import { atom } from 'jotai';
import { AgentActivityScope, SocketAgentActivity } from '../../../schemas/agent-conversation.schema';

/** Latest `agent_activity` frame per `(scope, agent)` — the "what's happening
 *  right now" line. Last-write-wins; entries are removed on `label: null`
 *  (ambient producers clear themselves) and every `turn` entry is dropped when
 *  the generation turn ends (complete/cancel). Lives in its own module (no
 *  component imports) so both the hooks and the indicator component can read it
 *  without an import cycle. */
export const AgentActivityAtom = atom<Record<string, SocketAgentActivity>>({});

export function agentActivityKey(frame: Pick<SocketAgentActivity, 'scope' | 'agent'>): string {
  return `${frame.scope}:${frame.agent ?? ''}`;
}

/** Fold an incoming frame into the activity map (last-write-wins / clear-on-null). */
export function reduceAgentActivity(
  current: Record<string, SocketAgentActivity>,
  frame: SocketAgentActivity
): Record<string, SocketAgentActivity> {
  const key = agentActivityKey(frame);
  if (frame.label == null) {
    if (!(key in current)) {
      return current;
    }
    const { [key]: _removed, ...rest } = current;
    return rest;
  }
  return { ...current, [key]: frame };
}

/** Drop all turn-scoped entries — the generation turn ended (complete/cancel). */
export function clearTurnActivity(
  current: Record<string, SocketAgentActivity>
): Record<string, SocketAgentActivity> {
  const entries = Object.entries(current).filter(([, frame]) => frame.scope !== AgentActivityScope.turn);
  if (entries.length === Object.keys(current).length) {
    return current;
  }
  return Object.fromEntries(entries);
}

/** The single line to render: `turn` beats `ambient` (while you're actively
 *  conversing, your turn's activity owns the line; ambient resurfaces after).
 *  Ambient labels are prefixed with the acting agent's name. */
export function pickActivityLabel(activity: Record<string, SocketAgentActivity>): string | undefined {
  const frames = Object.values(activity);
  const latest = (scope: AgentActivityScope) => {
    const scoped = frames.filter((f) => f.scope === scope && f.label != null);
    return scoped.length > 0 ? scoped[scoped.length - 1] : undefined;
  };
  const turn = latest(AgentActivityScope.turn);
  if (turn?.label != null) {
    return turn.label;
  }
  const ambient = latest(AgentActivityScope.ambient);
  if (ambient?.label != null) {
    return ambient.agent ? `${capitalize(ambient.agent)}: ${ambient.label}` : ambient.label;
  }
  return undefined;
}

/** Ambient-only variant of `pickActivityLabel` for surfaces outside the chat
 *  (the Atlas console header): turn-scoped frames are ignored entirely — a
 *  chat turn's tool activity must not hijack a dashboard header — and the
 *  agent-name prefix is dropped because the host surface already names the
 *  agent. Pass `agent` to scope the line to one producer, so another
 *  background agent's ambient frames can't surface misattributed on a surface
 *  that names a specific agent. */
export function pickAmbientActivityLabel(
  activity: Record<string, SocketAgentActivity>,
  agent?: string
): string | undefined {
  const ambient = Object.values(activity).filter(
    (f) =>
      f.scope === AgentActivityScope.ambient && f.label != null && (agent === undefined || f.agent === agent)
  );
  return ambient.length > 0 ? (ambient[ambient.length - 1].label ?? undefined) : undefined;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
